import { useContext, useEffect, useMemo } from 'react';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';
import { featureFlagListReducer, featureFlagListReducerName, featureFlagListInitialState } from '../Redux/FeatureFlag/reducer';
import { requestFeatureFlagList } from '../Redux/FeatureFlag/action';
import { requestFeatureFlagListSagas } from '../Services/FeatureService';
import { IFeatureFlagListAppState } from '../Redux/index';

export interface IFeatureFlags {
    [featureName: string]: boolean;
}

export interface IUseFeatureFlagListResult {
    isFeatureEnabled: IFeatureFlags;
    isReady: boolean;
}

const featureFlagListSagas = [requestFeatureFlagListSagas];
let hasDispatchedFeatureFlagListRequest = false;

/**
 * Custom hook to fetch and check feature flags.
 * Registers the FeatureFlagList dynamic reducer and saga, dispatches the request,
 * and returns a map of feature flags with their enabled status.
 *
 * Usage:
 *   const { isFeatureEnabled, isReady } = useFeatureFlagList();
 *   if (!isFeatureEnabled.IntakeFormTool_Feature) return <PageNotFound />;
 *   if (!isFeatureEnabled.SatReportsSecurity) return <PageNotFound />;
 */
export function useFeatureFlagList(): IUseFeatureFlagListResult {
    const { useSelector, dispatch } = useContext(Context as React.Context<IEmployeeExperienceContext>);

    useDynamicReducer(featureFlagListReducerName, featureFlagListReducer as any, featureFlagListSagas);

    const featureFlagListState = useSelector((state: IFeatureFlagListAppState) => state.dynamic?.[featureFlagListReducerName] || featureFlagListInitialState);
    const isReady = Boolean(featureFlagListState?.hasRequested && !featureFlagListState?.isLoading);
    const enabledFeatureFlags: string[] = featureFlagListState?.enabledFeatureFlags || [];

    // Fetch all feature flags
    useEffect(() => {
        if (!hasDispatchedFeatureFlagListRequest) {
            hasDispatchedFeatureFlagListRequest = true;
            dispatch(requestFeatureFlagList());
        }
    }, [dispatch]);

    // Build a map of feature flags: { featureName: true/false }
    const isFeatureEnabled: IFeatureFlags = useMemo(() => {
        const flagMap: IFeatureFlags = {};
        enabledFeatureFlags.forEach((flag) => {
            flagMap[flag] = true;
        });
        return flagMap;
    }, [enabledFeatureFlags]);

    return { isFeatureEnabled, isReady };
}